<template>
  <div id="tac-admin-customer-whos-shopping">
    <div class="page-header">
      <h1 class="page-header-title">
        <span class="fa fa-fw fa-shopping-cart"></span>
        Who's Shopping
      </h1>
      <div class="page-header-actions page-header-actions--right-position">
        <div class="form-inline form-inline--one-liner form-inline--better-spaced form-inline--compact">
          <div class="form-group">
            <search
              v-model="form.filter"
              placeholder="Search for..."
              :disabled="fetchingData"
              @clear="search"
              @execute="search"
              ></search>
          </div>
          <div class="form-group">
            <span class="form-inline__divider" />
          </div>
          <div class="form-group">
            <div class="btn-toolbar">
              <div class="btn-group">
                <button type="button" class="btn btn-default btn-sm" @click="reload" title="Refresh Page" :disabled="fetchingData"><i class="fa fa-fw fa-refresh"></i></button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="--page-body">
      <div class="tac-grid">
        <div class="tac-grid__item tac-grid__item-1" ref="active-shopper-grid">
          <table class="table table-bordered table-condensed">
            <caption>Customers</caption>
            <thead>
              <tr>
                <th class="col-lg-2 text-center">Customer #</th>
                <th class="col-lg-2">Type</th>
                <th>Customer Name</th>
              </tr>
            </thead>
            <tbody>
              <tr class="text-center text-info" v-if="!shoppers.length">
                <td colspan="6">No one is shopping today!</td>
              </tr>
              <template v-for="(shopper, key) in shoppers">
              <tr :class="{success: isShopperActive(shopper)}" :key="key" @click="onViewShopperLogs(shopper)" :ref="`active-shopper-${shopper.sessionId}`">
                <td class="text-center">{{ shopper.actorId || 'NO ID' }}</td>
                <td>{{ (shopper.actorType || 'UNKNOWN TYPE') | upperFirst }}</td>
                <td>{{ shopper.actorName || 'John Doe' }}</td>
              </tr>
              </template>
            </tbody>
            <tfoot>
              <tr v-show="isPaginationApplicable">
                <td colspan="12">
                  <pagination
                    class="text-center pagination-compact"
                    :p-total="pagination.total"
                    :p-per-page="pagination.perPage"
                    :p-page="pagination.page"
                    @open-page="openPage"
                    ></pagination>
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
        <div class="tac-grid__item tac-grid__item-2" ref="active-log-grid">
          <table class="table table-bordered table-condensed">
            <caption>Logs</caption>
            <thead>
              <tr>
                <th class="col-lg-3">Date</th>
                <th class="col-lg-1">Type</th>
                <th>Name</th>
              </tr>
            </thead>
            <tbody>
              <tr class="text-center text-info" v-if="!activeShopper || !activeShopper.logEntries.length">
                <td colspan="6">No log entered for this shopper!</td>
              </tr>
              <template v-if="activeShopper">
              <template v-for="(logEntry, key) in activeShopper.logEntries">
              <tr :class="{success: isLogEntryActive(logEntry)}" :key="key" @click="onViewLogEntry(logEntry)"  :ref="`active-log-entry-${logEntry.appLogId}`">
                <td>{{ formatDate(logEntry.appLogDatePosted) }}</td>
                <td>{{ logEntry.appLogType | upperFirst }}</td>
                <td>{{ logEntry.appLogName }}</td>
              </tr>
              </template>
              </template>
            </tbody>
          </table>
        </div>
        <div class="tac-grid__item tac-grid__item-3">
          <div v-if="activeLogEntry">
            <p>Log Details</p>
            <pre>{{ activeLogEntry }}</pre>
            <pre>{{ JSON.parse(activeLogEntry.appLogData) }}</pre>
            <pre>{{ JSON.parse(activeLogEntry.appLogMetadata) }}</pre>
          </div>
          <p v-else>No log entry selected!</p>
        </div>
      </div>
    </div>
    <vue-progress-bar></vue-progress-bar>
  </div><!-- #tac-admin-customer-whos-shopping -->
</template>

<script>
  import _ from 'lodash'
  import { mapState, mapGetters } from 'vuex'
  import moment from 'moment'
  import AuthMixin from '@/plugin/mixin/auth'
  import Pagination from '@/plugin/bootstrap/pagination';
  import Search from '@/plugin/element/search'
  import { upperFirst } from '@/plugin/filter/string'

  const FORM_MODEL = {
    filter: ''
  }

  export default {
    data() {
      return {
        form                : Object.assign({}, FORM_MODEL),
        selection           : {},
        activeShopper       : undefined,
        activeLogEntry      : undefined,
        initialized         : false,
        sorters             : {},
        isShopperBoxActive  : false,
        isLogEntryBoxActive : false
      }
    },
    created() {
      this.loadFormDataFromRoute()
      this.loadSortersFromRoute()
      this.loadPaginationFromRoute()
      this.updateSpec()
      this.$nextTick(() => {
        this.initialized = true
        this.fetchData()
      })
    },
    watch: {
      shoppers(newVal) {
        if (newVal.length > 0) {
          if (!this.activeShopper) {
            this.viewShopperLogs(_.first(newVal))
          }
        }
      },
      isShopperBoxActive(val) {
        if (val) {
          this.createArrowsKeydownListenerForShopper()
        } else {
          this.destroyArrowsKeydownListenerForShopper()
        }
      },
      isLogEntryBoxActive(val) {
        if (val) {
          this.createArrowsKeydownListenerForLogEntry()
        } else {
          this.destroyArrowsKeydownListenerForLogEntry()
        }
      },
    },
    computed: {
      ...mapState('whosShopping', {
        pagination: 'pagination',
        fetchingData: 'fetching'
      }),
      ...mapGetters('whosShopping', {
        shoppers: 'shoppers',
        isPaginationApplicable: 'isPaginationApplicable'
      }),
      isFormDefault() {
        return _.isEqual(this.form, FORM_MODEL)
      }
    },
    methods: {
      reload() {
        this.activeShopper = null
        this.activeLogEntry = null
        this.fetchData()
      },
      fetchData() {
        this.loadShoppers()
      },
      formatDate(date) {
        if (!date || '0000-00-00 00:00:00' === date) {
          return ''
        }

        return moment(date, 'YYYY-MM-DD HH:mm:ss').format('MMM DD, YYYY HH:mm:ss')
      },
      async search() {
        this.updateRoute()
        this.updateSpec()
        await this.loadShoppers()
      },
      async loadShoppers() {
        if (!this.initialized) {
          return
        }

        try {
          await this.$store.dispatch('whosShopping/fetchShoppers')
        } catch (e) {
          this.$alertify.alert('Shoppers not loaded')
        }
      },
      loadFormDataFromRoute() {
        const formModelKeys = _.keys(FORM_MODEL)
        const query         = _.get(this.$route, 'query')
        const queryKeys     = _.keys(query)

        _.forEach(queryKeys, (key) => {
          if (!_.includes(formModelKeys, key)) {
            return
          }

          this.form[key] = _.get(this.$route, ['query', key])
        })
      },
      loadSortersFromRoute() {
        const sorterKeys = _.keys(this.sorters)
        const query      = _.get(this.$route, 'query')
        const queryKeys  = _.keys(query)

        _.forEach(queryKeys, (key) => {
          if (!_.includes(sorterKeys, key)) {
            return
          }

          this.sorters[key] = _.get(this.$route, ['query', key])
        })
      },
      loadPaginationFromRoute() {
        const page = parseInt(this.$route.query.page)
        if (page && 1 !== page) {
          this.$store.dispatch('whosShopping/changePage', page)
        }
        const perPage = parseInt(this.$route.query.perPage)
        if (perPage && 10 !== perPage) {
          this.$store.dispatch('whosShopping/changeItemsPerPage', perPage)
        }
      },
      updateRoute() {
        let spec = Object.assign({}, this.sorters)
        if (!this.isFormDefault) {
          spec = Object.assign({}, spec, this.form)
        }

        spec = _.omitBy(spec, _.isNull)
        spec = _.omitBy(spec, v => !v)

        this.$router.push({
          name: 'admin/customer/whos-shopping',
          query: spec
        })
      },
      clearAllSorters() {
      },
      updateSpec() {
        let spec = Object.assign({}, this.form, this.sorters)

        spec = _.omitBy(spec, _.isNull)
        spec = _.omitBy(spec, v => !v)

        this.$store.dispatch('whosShopping/updateSpec', spec)
      },
      createArrowsKeydownListenerForShopper() {
        window.addEventListener('keydown', this.onGlobalArrowsKeydownForShopper)
      },
      destroyArrowsKeydownListenerForShopper() {
        window.removeEventListener('keydown', this.onGlobalArrowsKeydownForShopper)
      },
      createArrowsKeydownListenerForLogEntry() {
        window.addEventListener('keydown', this.onGlobalArrowsKeydownForLogEntry)
      },
      destroyArrowsKeydownListenerForLogEntry() {
        window.removeEventListener('keydown', this.onGlobalArrowsKeydownForLogEntry)
      },
      onGlobalArrowsKeydownForShopper(e) {
        if (true === e.ctrlKey || true === e.shiftKey  || true === e.altKey) {
          return
        }
        if ('ArrowUp' !== e.code && 'ArrowDown' !== e.code) {
          return
        }

        e.preventDefault()
        e.stopPropagation()

        if ('ArrowUp' === e.code) {
          this.selectPreviousShopper()
          this.scrollIntoViewShopper('up')
        } else if ('ArrowDown' === e.code) {
          this.selectNextShopper()
          this.scrollIntoViewShopper('down')
        }
      },
      selectNextShopper() {
        let shopper = this.$store.getters['whosShopping/nextShopper'](this.activeShopper)
        this.viewShopperLogs(shopper)
      },
      selectPreviousShopper() {
        let shopper = this.$store.getters['whosShopping/previousShopper'](this.activeShopper)
        this.viewShopperLogs(shopper)
      },
      onViewShopperLogs(shopper) {
        this.isLogEntryBoxActive = false
        this.isShopperBoxActive = true
        this.viewShopperLogs(shopper)
      },
      viewShopperLogs(shopper) {
        this.activeShopper = shopper
        this.activeLogEntry = undefined

        if (this.activeShopper && !this.activeLogEntry) {
          if (this.activeShopper.logEntries.length > 0) {
            this.viewLogEntry(_.first(this.activeShopper.logEntries))
          }
        }
      },
      isShopperActive(shopper) {
        return this.activeShopper === shopper
      },
      onGlobalArrowsKeydownForLogEntry(e) {
        if (true === e.ctrlKey || true === e.shiftKey  || true === e.altKey) {
          return
        }
        if ('ArrowUp' !== e.code && 'ArrowDown' !== e.code) {
          return
        }

        e.preventDefault()
        e.stopPropagation()

        if ('ArrowUp' === e.code) {
          this.selectPreviousLogEntry()
          this.scrollIntoViewLogEntry('up')
        } else if ('ArrowDown' === e.code) {
          this.selectNextLogEntry()
          this.scrollIntoViewLogEntry('down')
        }
      },
      selectNextLogEntry() {
        let logEntry = this.$store.getters['whosShopping/nextLogEntry'](this.activeShopper, this.activeLogEntry)
        this.viewLogEntry(logEntry)
      },
      selectPreviousLogEntry() {
        let logEntry = this.$store.getters['whosShopping/previousLogEntry'](this.activeShopper, this.activeLogEntry)
        this.viewLogEntry(logEntry)
      },
      onViewLogEntry(logEntry) {
        this.isShopperBoxActive = false
        this.isLogEntryBoxActive = true
        this.viewLogEntry(logEntry)
      },
      viewLogEntry(logEntry) {
        this.activeLogEntry = logEntry
      },
      isLogEntryActive(logEntry) {
        return this.activeLogEntry === logEntry
      },
      openPage(page) {
        this.$store.dispatch('whosShopping/changePage', page)

        let query = _.assign({}, this.$route.query, {page: page})
        if (query.page && 1 == query.page) {
          delete query.page
        }
        if (query.itemsPerPage && 10 == query.itemsPerPage) {
          delete query.itemsPerPage
        }

        this.$router.push({
          name   : this.$route.name,
          params : _.assign({}, this.$route.params),
          query  : _.assign({}, query),
        })

        this.fetchData()
      },
      scrollIntoViewShopper(direction) {
        const rowRef = this.$refs[`active-shopper-${this.activeShopper.sessionId}`]
        if (!rowRef) {
          return
        }

        const gridRef = this.$refs['active-shopper-grid']
        if (!gridRef) {
          return
        }

        const rowEl = rowRef[0]
        const rowRect = rowEl.getBoundingClientRect()

        const gridEl = gridRef
        const gridRect = gridEl.getBoundingClientRect()

        let position = rowRect.top - gridRect.top
        if ('down' === direction) {
          position = position + rowRect.height
        }

        if (position > 0 && position <= gridRect.height) {
          return
        }

        rowEl.scrollIntoView()
      },
      scrollIntoViewLogEntry(direction) {
        const rowRef = this.$refs[`active-log-entry-${this.activeLogEntry.appLogId}`]
        if (!rowRef) {
          return
        }

        const gridRef = this.$refs['active-log-grid']
        if (!gridRef) {
          return
        }

        const rowEl = rowRef[0]
        const rowRect = rowEl.getBoundingClientRect()

        const gridEl = gridRef
        const gridRect = gridEl.getBoundingClientRect()

        let position = rowRect.top - gridRect.top
        if ('down' === direction) {
          position = position + rowRect.height
        }

        if (position > 0 && position <= gridRect.height) {
          return
        }

        rowEl.scrollIntoView()
      }
    },
    components: {
      Pagination,
      Search
    },
    filters: {
      upperFirst
    },
    mixins: [
      AuthMixin
    ]
  }
</script>
